/**
 * Capture on open: a completed form locked without a snapshot gets one the first time someone
 * opens it (#6413).
 *
 * The rendering happens in BnSnapshotCaptureHost, mounted once in App.vue, which registers itself
 * here. ensureFormSnapshots renders each missing snapshot from the same print data the logs print
 * from, uploads it marked as captured after lock, and returns the snapshot ids for the caller to
 * open. A null result tells the caller to open the live form as it would without a snapshot.
 */

import { fetchFormPrintData } from '@/components/questionnaires/form/print/formPrintData';
import { captureSnapshot, uploadSnapshot } from '@/common/formSnapshot';
import { useSnapshotGate } from '@/composables/useSnapshotGate';
import { $log } from '@/common/api/log.service';
import { LOG_ERROR } from '@/common/log/log';

let captureHost = null;

/** @param {{ render: (printData: object) => Promise<string>, setProgress: (message: string|null) => void }} host */
export function registerSnapshotCaptureHost(host) {
    captureHost = host;
}

export function unregisterSnapshotCaptureHost(host) {
    if (captureHost === host) captureHost = null;
}

const progressMessage = (index, total) => (total > 1 ? `Processing snapshot ${index + 1} of ${total}` : 'Preparing snapshot…');

const captureOne = async (host, qr, groupNote) => {
    const [entry] = await fetchFormPrintData([{ qrId: qr.id, canonical: qr.questionnaire }]);
    if (!entry) throw new Error('Print data not found');
    const printData = { ...entry, snapshotMode: true, groupItemFilter: groupNote ? 'participant' : '' };
    const { html } = await captureSnapshot({
        getSnapshotHtml: async () => {
            const rendered = await host.render(printData);
            if (typeof rendered !== 'string' || !rendered.trim()) throw new Error('Snapshot render was empty');
            return rendered;
        },
    });
    const { documentReferenceId } = await uploadSnapshot(qr.id, html, groupNote ? 'participant' : null, { afterLock: true });
    if (!documentReferenceId) throw new Error('Snapshot upload returned no id');
    return documentReferenceId;
};

/**
 * Make sure every completed response in `qrs` has a snapshot, capturing the missing ones.
 *
 * @param {object[]} qrs QuestionnaireResponses behind the open (one form, or a group note's participants)
 * @param {{ groupNote?: boolean }} [options] groupNote captures each as a participant snapshot
 * @returns {Promise<string[]|null>} the completed responses' DocumentReference ids in input order,
 *   or null when capture is unavailable or anything failed
 */
export async function ensureFormSnapshots(qrs, { groupNote = false } = {}) {
    const host = captureHost;
    const completed = (qrs || []).filter((qr) => qr?.status === 'completed');
    if (!useSnapshotGate().isSnapshotsEnabled.value || !host || !completed.length) return null;

    const missing = completed.filter((qr) => !qr.formPdfSnapshot?.id);
    const captured = new Map();
    let current = null;
    try {
        for (const [index, qr] of missing.entries()) {
            current = qr;
            host.setProgress(progressMessage(index, missing.length));
            // One capture at a time: they share the host's single renderer.
            // eslint-disable-next-line no-await-in-loop
            captured.set(qr.id, await captureOne(host, qr, groupNote));
        }
    } catch (error) {
        $log.log(LOG_ERROR, 'Form snapshot capture on open failed', `QuestionnaireResponse ${current?.id} (of ${missing.map((qr) => qr.id).join(', ')}): ${error?.message || error}`);
        return null;
    } finally {
        host.setProgress(null);
    }

    return completed.map((qr) => qr.formPdfSnapshot?.id || captured.get(qr.id));
}
